import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import myhomeLogo from '../assests/myhome-logo.png';

const safeText = (value) => (
  value === null || value === undefined || value === '' ? '-' : String(value)
);

const escapeHtml = (value) => safeText(value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? safeText(value) : date.toLocaleString();
};

const formatAmount = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return `₹${safeText(value)}`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatLabel = (value) => safeText(value)
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/_/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const formatReceiptDate = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return safeText(value);
  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  }).format(date);
  return `${datePart}, ${timePart}`;
};

const formatMonthYear = (payment = {}) => {
  if (payment.paymentType === 'Outstanding' || payment.year == null || payment.month == null) {
    return 'Outstanding';
  }
  const monthName = new Date(payment.year, (payment.month || 1) - 1)
    .toLocaleString('default', { month: 'long' });
  return `${monthName} ${payment.year}`;
};

const formatBlockName = (blockName) => {
  const text = safeText(blockName);
  if (text === '-') return null;
  return /\bblock\b/i.test(text) ? text : `${text} Block`;
};

const receiptIcon = (path, className = '') => `
  <svg class="row-icon ${className}" viewBox="0 0 24 24" aria-hidden="true">
    <path d="${path}" />
  </svg>
`;

const receiptIcons = {
  receipt: 'M6 2h9l5 5v15H6V2zm8 2v4h4M9 12h8M9 16h8',
  building: 'M4 22V6l8-4v20M4 10h8M7 7v2M10 6v3M7 12v2M10 12v2M7 17v2M10 17v2M12 10h8v12M15 13v2M18 13v2M15 18v2M18 18v2',
  home: 'M3 11l9-8 9 8M5 10v11h14V10M9 21v-7h6v7',
  person: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm-7 9a7 7 0 0 1 14 0',
  amount: 'M7 4h11M7 9h11M8 4c6 0 6 8 0 8h3l7 8M7 12h5',
  type: 'M6 2h9l5 5v15H6V2zm8 2v4h4M9 12h8M9 16h8',
  calendar: 'M5 3v3M19 3v3M3 9h18M5 5h14a2 2 0 0 1 2 2v14H3V7a2 2 0 0 1 2-2zM7 13h3M14 13h3M7 17h3M14 17h3',
  card: 'M3 6h18v13H3V6zm0 4h18M7 15h4',
  status: 'M5 13l4 4L19 7',
  due: 'M5 3v3M19 3v3M3 9h18M5 5h14a2 2 0 0 1 2 2v14H3V7a2 2 0 0 1 2-2zM15 14v3l2 1',
  reference: 'M10 3L7 21M17 3l-3 18M4 9h16M3 15h16',
  description: 'M21 12a9 9 0 0 1-9 9 9.5 9.5 0 0 1-4-.9L3 21l.9-5A9 9 0 1 1 21 12z'
};

const receiptRow = (label, value, icon = 'receipt', valueClass = '') => `
  <tr>
    <td class="icon-cell">${receiptIcon(receiptIcons[icon] || receiptIcons.receipt, icon === 'status' ? 'success-icon' : '')}</td>
    <th>${escapeHtml(label)}</th>
    <td class="separator">:</td>
    <td class="value ${valueClass}">${escapeHtml(value)}</td>
  </tr>
`;

const assetToDataUrl = async (assetUrl) => {
  try {
    const response = await fetch(assetUrl);
    if (!response.ok) return assetUrl;
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Unable to embed receipt asset.', error);
    return assetUrl;
  }
};

const downloadInBrowser = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const copyReceiptText = async (receiptText) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(receiptText);
    return true;
  }
  return false;
};

const saveReceiptFile = async (html, fileName, directory = Directory.Documents) => {
  const folder = 'MyHomeEntry/Receipts';
  const path = `${folder}/${fileName}`;

  try {
    await Filesystem.mkdir({
      path: folder,
      directory,
      recursive: true
    });
  } catch (error) {
    // Folder may already exist.
  }

  await Filesystem.writeFile({
    path,
    data: html,
    directory,
    encoding: Encoding.UTF8,
    recursive: true
  });

  const uri = await Filesystem.getUri({
    path,
    directory
  });

  return { path, uri: uri.uri };
};

const shareReceiptFile = async (html, fileName, receiptNo) => {
  let savedFile;
  try {
    savedFile = await saveReceiptFile(html, fileName, Directory.Cache);
  } catch (cacheError) {
    savedFile = await saveReceiptFile(html, fileName, Directory.Documents);
  }

  await Share.share({
    title: `Payment Receipt - ${safeText(receiptNo)}`,
    text: 'MyHomeEntry payment receipt',
    url: savedFile.uri,
    dialogTitle: 'Share payment receipt'
  });

  return savedFile;
};

const showReceiptInApp = (html, fileName, receiptNo) => {
  if (!document?.body) return false;

  const previousOverflow = document.body.style.overflow;
  const overlay = document.createElement('div');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-label', 'Payment receipt');
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    z-index: 3000;
    display: flex;
    flex-direction: column;
    background: #f5f7fa;
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
  `;

  const toolbar = document.createElement('div');
  toolbar.style.cssText = `
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 56px;
    padding: 8px 12px;
    background: #1976d2;
    color: #fff;
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  `;

  const title = document.createElement('div');
  title.textContent = 'Receipt';
  title.style.cssText = `
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font: 600 16px Arial, sans-serif;
  `;

  const actionButtonStyle = `
    border: 1px solid rgba(255,255,255,0.7);
    border-radius: 4px;
    background: transparent;
    color: #fff;
    padding: 8px 12px;
    font: 600 14px Arial, sans-serif;
  `;

  const downloadButton = document.createElement('button');
  downloadButton.type = 'button';
  downloadButton.textContent = 'Download';
  downloadButton.style.cssText = actionButtonStyle;

  const shareButton = document.createElement('button');
  shareButton.type = 'button';
  shareButton.textContent = 'Share';
  shareButton.style.cssText = actionButtonStyle;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = 'Close';
  closeButton.style.cssText = `
    border: 0;
    border-radius: 4px;
    background: #fff;
    color: #1976d2;
    padding: 8px 12px;
    font: 600 14px Arial, sans-serif;
  `;

  const notice = document.createElement('div');
  notice.style.cssText = `
    position: fixed;
    left: 16px;
    right: 16px;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px));
    z-index: 3001;
    display: none;
    padding: 12px 14px;
    border-radius: 6px;
    background: rgba(32, 43, 54, 0.94);
    color: #fff;
    text-align: center;
    font: 500 14px Arial, sans-serif;
    box-shadow: 0 4px 14px rgba(0,0,0,0.25);
  `;

  let noticeTimer;
  const showNotice = (message) => {
    window.clearTimeout(noticeTimer);
    notice.textContent = message;
    notice.style.display = 'block';
    noticeTimer = window.setTimeout(() => {
      notice.style.display = 'none';
    }, 2200);
  };

  const content = document.createElement('div');
  content.style.cssText = `
    flex: 1;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-y;
    background: #f5f7fa;
  `;
  content.innerHTML = html;
  const receiptText = content.textContent
    ?.replace(/\s+/g, ' ')
    .trim() || `Payment receipt ${safeText(receiptNo)}`;

  const closeViewer = () => {
    document.body.style.overflow = previousOverflow;
    overlay.remove();
  };

  downloadButton.addEventListener('click', async () => {
    const originalText = downloadButton.textContent;
    downloadButton.disabled = true;
    downloadButton.textContent = 'Saving...';
    try {
      const savedFile = await saveReceiptFile(html, fileName, Directory.Documents);
      showNotice(`Receipt saved to ${savedFile.path}`);
    } catch (error) {
      console.error('Unable to save receipt.', error);
      showNotice('Unable to save receipt on this device.');
    } finally {
      downloadButton.disabled = false;
      downloadButton.textContent = originalText;
    }
  });

  shareButton.addEventListener('click', async () => {
    const originalText = shareButton.textContent;
    shareButton.disabled = true;
    shareButton.textContent = 'Sharing...';
    try {
      await shareReceiptFile(html, fileName, receiptNo);
      showNotice('Receipt ready to share.');
    } catch (error) {
      console.error('Unable to share receipt file.', error);
      try {
        const copied = await copyReceiptText(receiptText);
        showNotice(copied ? 'Receipt details copied instead.' : 'Unable to share receipt on this device.');
      } catch (fallbackError) {
        console.error('Unable to copy receipt fallback.', fallbackError);
        showNotice('Unable to share receipt on this device.');
      }
    } finally {
      shareButton.disabled = false;
      shareButton.textContent = originalText;
    }
  });

  closeButton.addEventListener('click', closeViewer);
  overlay.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeViewer();
  });

  toolbar.appendChild(title);
  toolbar.appendChild(downloadButton);
  toolbar.appendChild(shareButton);
  toolbar.appendChild(closeButton);
  overlay.appendChild(toolbar);
  overlay.appendChild(content);
  overlay.appendChild(notice);
  document.body.style.overflow = 'hidden';
  document.body.appendChild(overlay);
  closeButton.focus();
  return true;
};

const openReceiptOnMobile = async (html, blob, fileName, receiptNo) => {
  try {
    if (showReceiptInApp(html, fileName, receiptNo)) return true;
  } catch (error) {
    console.warn('In-app receipt viewer is not available, opening receipt instead.', error);
  }

  try {
    const dataUrl = `data:text/html;charset=utf-8,${encodeURIComponent(html)}`;
    await Browser.open({ url: dataUrl });
    return true;
  } catch (error) {
    console.warn('Capacitor Browser could not open receipt, using window fallback.', error);
  }

  try {
    const url = URL.createObjectURL(blob);
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return Boolean(opened);
  } catch (error) {
    console.error('Unable to open receipt on this device.', error);
    return false;
  }
};

export async function downloadPaymentReceipt(payment = {}, options = {}) {
  const flat = options.flat || {};
  const resident = options.resident || {};
  const receiptNo = payment.paymentId || `PAYMENT-${Date.now()}`;
  const paidOn = payment.paidDate || payment.updatedAt || payment.createdAt;
  const amount = payment.amount ?? payment.paymentAmount ?? '';
  const formattedAmount = formatAmount(amount);
  const paymentType = formatLabel(payment.paymentType || '-');
  const paymentStatus = formatLabel(payment.status || '-');
  const normalizedStatus = safeText(payment.status).toLowerCase();
  const statusClass = ['completed', 'success', 'successful', 'paid', 'shipped'].includes(normalizedStatus)
    ? 'status-success'
    : 'status-neutral';
  const isSuccessful = statusClass === 'status-success';
  const statusHeading = isSuccessful ? 'Payment Completed Successfully' : `Payment ${paymentStatus}`;
  const statusMessage = isSuccessful ? 'Thank you for your payment.' : 'Please review the payment status below.';
  const logoDataUrl = await assetToDataUrl(myhomeLogo);
  const generatedOn = formatReceiptDate();
  const flatLabel = options.flatLabel
    || [flat.flatNumber ? `Flat ${flat.flatNumber}` : null, formatBlockName(flat.blockName)]
      .filter(Boolean)
      .join(', ')
    || payment.flatNumber
    || payment.flatId
    || '-';

  const rows = [
    receiptRow('Receipt / Payment ID', receiptNo, 'receipt', 'identifier'),
    receiptRow('Building', options.buildingName || flat.buildingName || payment.buildingName || '-', 'building'),
    receiptRow('Flat', flatLabel, 'home'),
    receiptRow('Resident', options.residentName || resident.name || resident.fullName || '-', 'person'),
    receiptRow('Payment Type', paymentType, 'type'),
    receiptRow('Payment Period', formatMonthYear(payment), 'calendar'),
    receiptRow('Due Date', formatDate(payment.dueDate), 'due'),
    receiptRow('Paid Date', formatDate(paidOn), 'calendar'),
    receiptRow('Reference', payment.transactionId || payment.razorpayPaymentId || payment.ccavenueTrackingId || payment.bankReference || '-', 'reference'),
    receiptRow('Description', payment.description || '-', 'description')
  ].join('');

  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1, viewport-fit=cover" />
  <title>Payment Receipt - ${escapeHtml(receiptNo)}</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #0b2052; margin: 0; padding: 20px; background: #f4f9ff; }
    .receipt { width: 100%; max-width: 980px; margin: 0 auto; padding: 0 16px 22px; background: #fff; border: 1px solid #d8e8f7; border-radius: 18px; overflow: hidden; box-shadow: 0 14px 38px rgba(25, 91, 155, 0.13); }
    .header { position: relative; min-height: 172px; margin: 0 -16px; padding: 26px 34px 62px; overflow: hidden; color: #fff; background: linear-gradient(120deg, #087ce8 0%, #2496f2 72%, #55b8f4 100%); }
    .header-content { position: relative; z-index: 2; display: flex; align-items: center; gap: 28px; }
    .brand-logo { width: 220px; max-width: 31%; height: auto; border-radius: 4px; }
    .header-divider { align-self: stretch; width: 1px; min-height: 92px; background: rgba(255,255,255,0.65); }
    .header-copy { min-width: 0; }
    .header h1 { margin: 0 0 8px; font-size: 34px; line-height: 1.1; letter-spacing: -0.5px; }
    .header .tagline { margin: 0 0 7px; font-size: 17px; font-weight: 600; }
    .header .company { margin: 0; font-size: 14px; opacity: 0.94; }
    .skyline { position: absolute; z-index: 1; right: -8px; bottom: 0; width: 360px; height: 140px; opacity: 0.66; }
    .summary { position: relative; z-index: 3; display: grid; grid-template-columns: minmax(260px, 1.6fr) repeat(3, minmax(130px, 1fr)); margin: -46px 0 20px; padding: 17px 22px; background: #fff; border: 1px solid #e0ebf5; border-radius: 13px; box-shadow: 0 8px 22px rgba(35, 92, 149, 0.11); }
    .summary-lead { display: flex; align-items: center; gap: 16px; min-width: 0; padding-right: 20px; }
    .success-mark { display: grid; flex: 0 0 58px; width: 58px; height: 58px; place-items: center; border-radius: 50%; color: #fff; background: #08b879; box-shadow: 0 7px 17px rgba(8,184,121,0.22); }
    .success-mark.neutral { background: #6b7b91; box-shadow: none; }
    .success-mark svg { width: 30px; height: 30px; fill: none; stroke: currentColor; stroke-width: 3; stroke-linecap: round; stroke-linejoin: round; }
    .summary-heading { margin: 0 0 5px; color: #0c1c50; font-size: 18px; line-height: 1.25; }
    .summary-copy { margin: 0; color: #66749a; font-size: 14px; }
    .summary-item { min-width: 0; display: flex; flex-direction: column; justify-content: center; padding: 0 20px; border-left: 1px solid #dce4ef; }
    .summary-label { display: block; margin-bottom: 7px; color: #62709a; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .summary-value { display: block; color: #07194d; font-size: 17px; font-weight: 700; overflow-wrap: anywhere; }
    .summary-amount { color: #0a55c8; font-size: 28px; }
    .status { display: inline-flex; align-items: center; gap: 7px; padding: 6px 10px; border-radius: 999px; font-size: 13px; font-weight: 700; }
    .status::before { content: ''; width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
    .status-success { color: #078c4b; background: #def7e9; }
    .status-neutral { color: #566477; background: #eef2f6; }
    .details { padding: 14px; border: 1px solid #dce9f5; border-radius: 13px; }
    .details-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 0 2px 10px; }
    .details-title { display: flex; align-items: center; gap: 12px; }
    .details-title-icon { display: grid; width: 42px; height: 42px; place-items: center; border-radius: 50%; color: #087dea; background: #e7f3ff; }
    .details-title-icon svg { width: 23px; height: 23px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
    .section-title { margin: 0; color: #0a1c52; font-size: 21px; }
    .generated-date { color: #65739b; font-size: 12px; text-align: right; }
    table { width: 100%; border-collapse: separate; border-spacing: 0 3px; table-layout: fixed; }
    tr:nth-child(odd) { background: #f3f8fd; }
    th, td { padding: 8px 7px; text-align: left; vertical-align: middle; overflow-wrap: anywhere; word-break: break-word; }
    tr > :first-child { border-radius: 7px 0 0 7px; }
    tr > :last-child { border-radius: 0 7px 7px 0; }
    .icon-cell { width: 48px; color: #087dea; text-align: center; }
    .row-icon { width: 22px; height: 22px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; vertical-align: middle; }
    .success-icon { color: #08a960; }
    th { width: 25%; color: #14285c; font-size: 14px; font-weight: 500; }
    .separator { width: 28px; color: #7180a5; text-align: center; }
    .value { color: #0d2259; font-size: 14px; }
    .identifier { font-size: 13px; }
    .amount-value { font-weight: 700; }
    .success-value { color: #06934d; font-weight: 700; }
    .footer { display: grid; grid-template-columns: 1fr; gap: 22px; margin-top: 20px; padding: 19px 24px; color: #66749a; background: #eaf5ff; border-radius: 12px; }
    .footer-block { display: flex; align-items: center; gap: 14px; min-width: 0; }
    .footer-block + .footer-block { padding-left: 22px; border-left: 1px solid #a9c7e5; }
    .footer-icon { flex: 0 0 auto; color: #0877e4; font-size: 30px; line-height: 1; }
    .footer strong { display: block; margin-bottom: 4px; color: #0565d6; font-size: 15px; }
    .footer span { font-size: 12px; line-height: 1.45; }
    .info-circle { display: grid; width: 30px; height: 30px; place-items: center; border: 2px solid #0877e4; border-radius: 50%; font-size: 18px; font-weight: 700; }
    @media (max-width: 760px) {
      body { padding: 10px; }
      .receipt { padding: 0 10px 14px; border-radius: 12px; }
      .header { min-height: 150px; margin: 0 -10px; padding: 20px 16px 54px; }
      .header-content { gap: 14px; }
      .brand-logo { width: 135px; max-width: 38%; }
      .header-divider { min-height: 72px; }
      .header h1 { font-size: 24px; }
      .header .tagline { font-size: 13px; }
      .header .company { font-size: 11px; }
      .skyline { width: 245px; height: 105px; opacity: 0.45; }
      .summary { grid-template-columns: 1fr 1fr; margin-top: -40px; padding: 15px; }
      .summary-lead { grid-column: 1 / -1; padding: 0 0 14px; border-bottom: 1px solid #dce4ef; }
      .summary-item { padding: 12px 8px 0; border-left: 0; }
      .summary-item:nth-child(3) { border-left: 1px solid #dce4ef; }
      .summary-item:last-child { grid-column: 1 / -1; padding-top: 15px; }
      .summary-amount { font-size: 24px; }
      .details { padding: 10px; }
      .details-heading { align-items: flex-start; }
      .section-title { font-size: 18px; }
      .generated-date { max-width: 140px; }
      table { table-layout: auto; }
      .icon-cell { width: 35px; padding-left: 4px; }
      th { width: 34%; font-size: 12px; }
      .separator { display: none; }
      .value { font-size: 12px; }
      .footer { grid-template-columns: 1fr; padding: 16px; }
      .footer-block + .footer-block { padding: 14px 0 0; border-top: 1px solid #a9c7e5; border-left: 0; }
    }
    @media (max-width: 420px) {
      body { padding: 0; }
      .receipt { border: 0; border-radius: 0; box-shadow: none; }
      .header { min-height: 142px; }
      .header-content { align-items: flex-start; gap: 11px; }
      .brand-logo { width: 105px; max-width: 34%; }
      .header-divider { min-height: 65px; }
      .header h1 { font-size: 20px; }
      .header .tagline { font-size: 12px; }
      .header .company { font-size: 10px; line-height: 1.35; }
      .success-mark { flex-basis: 48px; width: 48px; height: 48px; }
      .summary-heading { font-size: 16px; }
      .details-heading { display: block; }
      .generated-date { display: block; max-width: none; margin: 8px 0 0 54px; text-align: left; }
      .icon-cell { width: 31px; }
      .row-icon { width: 19px; height: 19px; }
      th { width: 31%; }
      .identifier { font-size: 11px; }
    }
    @media print {
      body { padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .receipt { max-width: none; border: none; border-radius: 0; box-shadow: none; }
    }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="header">
      <svg class="skyline" viewBox="0 0 420 150" aria-hidden="true">
        <g fill="#fff">
          <circle cx="74" cy="55" r="22" opacity=".18"/><circle cx="105" cy="52" r="30" opacity=".18"/><circle cx="139" cy="62" r="19" opacity=".18"/>
          <path d="M132 150V90l42-18v78zm54 0V64l60-28 54 22v92zm126 0V45l54-22 48 19v108z" opacity=".92"/>
        </g>
        <g fill="#1c8bd4" opacity=".65">
          <path d="M145 102h17v15h-17zm0 23h17v15h-17zm55-46h18v18h-18zm28-13h18v19h-18zm0 31h18v19h-18zm-28 9h18v19h-18zm28 19h18v19h-18zm99-65h18v19h-18zm26-10h18v19h-18zm26 8h18v19h-18zm-52 31h18v19h-18zm26-10h18v19h-18zm26 8h18v19h-18zm-52 31h18v19h-18zm26-10h18v19h-18zm26 8h18v19h-18z"/>
        </g>
        <g fill="#24a95a"><circle cx="276" cy="128" r="23"/><circle cx="302" cy="136" r="18"/><circle cx="404" cy="130" r="22"/></g>
      </svg>
      <div class="header-content">
        <img class="brand-logo" src="${escapeHtml(logoDataUrl)}" alt="MyHomeEntry" />
        <div class="header-divider"></div>
        <div class="header-copy">
          <h1>Payment Receipt</h1>
          <p class="tagline">My Home Entry</p>
          <p class="company">A product of Innoture Solutions Private Limited</p>
        </div>
      </div>
    </div>
    <div class="summary">
      <div class="summary-lead">
        <span class="success-mark ${isSuccessful ? '' : 'neutral'}">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="${isSuccessful ? 'M5 12.5l4 4L19 7' : 'M12 7v6M12 17h.01'}" /></svg>
        </span>
        <div>
          <h2 class="summary-heading">${escapeHtml(statusHeading)}</h2>
          <p class="summary-copy">${escapeHtml(statusMessage)}</p>
        </div>
      </div>
      <div class="summary-item">
        <span class="summary-label">Amount paid</span>
        <span class="summary-value summary-amount">${escapeHtml(formattedAmount)}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Status</span>
        <span class="status ${statusClass}">${escapeHtml(paymentStatus)}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Payment method</span>
        <span class="summary-value">${escapeHtml(payment.paymentMethod || '-')}</span>
      </div>
    </div>
    <div class="details">
      <div class="details-heading">
        <div class="details-title">
          <span class="details-title-icon">${receiptIcon(receiptIcons.receipt)}</span>
          <h2 class="section-title">Payment Details</h2>
        </div>
        <span class="generated-date">Receipt generated on ${escapeHtml(generatedOn)}</span>
      </div>
      <table>${rows}</table>
    </div>
    <div class="footer">
      <div class="footer-block">
        <span class="footer-icon" aria-hidden="true">&#127881;</span>
        <span><strong>Thank you for your payment!</strong>This is a system-generated receipt.</span>
      </div>
    </div>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const fileName = `payment-receipt-${safeText(receiptNo).replace(/[^\w.-]+/g, '_')}.html`;

  if (Capacitor.isNativePlatform()) {
    const handled = await openReceiptOnMobile(html, blob, fileName, receiptNo);
    if (handled) return;
  }

  downloadInBrowser(blob, fileName);
}
